import React from 'react'
import { SelectboxProps, Selectbox, MultiCombobox, MultiComboboxProps, ComboboxProps, Combobox } from 'view/components/options'
import { connect } from 'react-redux'
import { EntityTypeFor, EntityHelpers } from 'model'
import { EntityType } from 'api'
import { createStructuredSelector } from 'reselect'
import { createEntityGetterSelector, EntityGetter } from 'selectors/entitySelectors'
import { GlobalState } from 'store'
import { ParametricSelector } from 'utils/redux'

interface EntitySelectboxProps<T> extends Omit<SelectboxProps<T, string>, 'getOption' | 'getOptionText'> {
  entityGetter: (id: string) => T | null
}

function createEntitySelectboxComponent<TEntityType extends EntityType>(
  entityType: TEntityType
): React.FunctionComponent<EntitySelectboxProps<EntityTypeFor<TEntityType>>> {
  return props => {
    const { entityGetter, ...rest } = props
    const dataHelper = EntityHelpers[entityType]

    return <Selectbox getOption={(key: string) => entityGetter(key)} getOptionText={option => dataHelper.getName(option)} {...rest} />
  }
}

export function createEntitySelectbox<TEntityType extends EntityType>(entityType: TEntityType) {
  const mapStateToProps = createStructuredSelector({
    entityGetter: createEntityGetterSelector(entityType)
  })

  const EntitySelectbox = createEntitySelectboxComponent(entityType)

  return connect(mapStateToProps)(EntitySelectbox)
}

interface EntityMultiComboboxProps<T> extends Omit<MultiComboboxProps<T, string>, 'getOption'> {
  entityGetter: (id: string) => T | null
}

function createEntityMultiComboboxComponent<TEntityType extends EntityType>(
  entityType: TEntityType
): React.FunctionComponent<EntityMultiComboboxProps<EntityTypeFor<TEntityType>>> {
  return props => {
    const { entityGetter, ...rest } = props
    const dataHelper = EntityHelpers[entityType]

    return <MultiCombobox getOption={(key: string) => entityGetter(key)} getOptionText={option => dataHelper.getName(option)} {...rest} />
  }
}

export function createEntityMultiCombobox<TEntityType extends EntityType>(entityType: TEntityType) {
  const mapStateToProps = createStructuredSelector({
    entityGetter: createEntityGetterSelector(entityType) as ParametricSelector<
      GlobalState,
      EntitySelectboxProps<TEntityType>,
      EntityGetter<EntityTypeFor<TEntityType>>
    >
  })

  return connect(mapStateToProps)(createEntityMultiComboboxComponent(entityType))
}

interface EntityComboboxProps<T> extends ComboboxProps<T, string> {
  entityGetter: (id: string) => T | null
}

function createEntityComboboxComponent<TEntityType extends EntityType>(
  entityType: TEntityType
): React.FunctionComponent<EntityComboboxProps<EntityTypeFor<TEntityType>>> {
  return props => {
    const { entityGetter, ...rest } = props
    const dataHelper = EntityHelpers[entityType]

    return <Combobox getOption={(key: string) => entityGetter(key)} getOptionText={option => dataHelper.getName(option)} {...rest} />
  }
}

export function createEntityCombobox<TEntityType extends EntityType>(entityType: TEntityType) {
  const mapStateToProps = createStructuredSelector({
    entityGetter: createEntityGetterSelector(entityType)
  })

  return connect(mapStateToProps)(createEntityComboboxComponent(entityType))
}
